import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { api } from "../../../config/api";

const { Option } = Select;

const Caixas = ({ handleCloseModal }) => {
  const accessToken = window.localStorage.getItem("accessToken");
  const idUser = window.localStorage.getItem("idUser");

  const [loja, setLoja] = useState(null);
  const [responsaveis, setResponsaveis] = useState([]);

  const [caixas, setCaixas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLoja, setLoadingLoja] = useState(false);
  const [loadingResponsaveis, setLoadingResponsaveis] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [caixaEmEdicao, setCaixaEmEdicao] = useState(null);

  const [descricao, setDescricao] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [idUsuarioSelecionado, setIdUsuarioSelecionado] = useState(undefined);
  const [busca, setBusca] = useState("");

  const CAIXA_URL = `${api.techspdv}/api/caixas`;

  const getOptions = (idLoja = null) => ({
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "Bearer " + accessToken,
    },
    ...(idLoja
      ? {
        params: {
          idLoja,
        },
      }
      : {}),
  });

  const carregarLoja = async () => {
    if (!accessToken || !idUser) return;

    setLoadingLoja(true);

    try {
      const response = await axios.get(
        `${api.techfood}/v1/restaurantes/usuario/${idUser}`,
        getOptions()
      );

      setLoja(response.data);
    } catch (error) {
      console.error("Erro ao carregar loja:", error);
      setLoja(null);

      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Não foi possível identificar a loja do usuário."
      );
    } finally {
      setLoadingLoja(false);
    }
  };

  const carregarResponsaveis = async (idLoja) => {
    if (!accessToken || !idLoja) return;

    setLoadingResponsaveis(true);

    try {
      const response = await axios.get(
        `${api.techfood}/v1/restaurantes/${idLoja}/responsaveis`,
        getOptions()
      );

      const dados =
        response?.data?._embedded?.responsaveis ||
        response?.data?._embedded?.Responsavel ||
        response?.data?._embedded?.usuarios ||
        response?.data?.content ||
        response?.data ||
        [];

      setResponsaveis(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar responsáveis:", error);
      setResponsaveis([]);

      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Não foi possível carregar os usuários da loja."
      );
    } finally {
      setLoadingResponsaveis(false);
    }
  };

  const carregarCaixas = async (idLojaParam = loja?.id) => {
    if (!accessToken || !idLojaParam) return;

    setLoading(true);

    try {
      const response = await axios.get(
        CAIXA_URL,
        getOptions(idLojaParam)
      );

      const dados =
        response?.data?._embedded?.caixas ||
        response?.data?._embedded?.Caixa ||
        response?.data?.content ||
        response?.data ||
        [];

      setCaixas(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar caixas:", error);
      setCaixas([]);

      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Não foi possível carregar os caixas desta loja."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarLoja();
  }, [accessToken, idUser]);

  useEffect(() => {
    if (loja?.id) {
      carregarCaixas(loja.id);
      carregarResponsaveis(loja.id);
    } else {
      setCaixas([]);
      setResponsaveis([]);
    }
  }, [loja?.id]);

  const getResponsavelId = (responsavel) =>
    responsavel?.id ||
    responsavel?.idUsuario ||
    responsavel?.usuario?.id ||
    responsavel?.userId;

  const getResponsavelNome = (responsavel) =>
    responsavel?.nome ||
    responsavel?.nomeUsuario ||
    responsavel?.usuario?.nome ||
    responsavel?.name ||
    responsavel?.email ||
    getResponsavelId(responsavel) ||
    "Usuário";

  const getResponsavelEmail = (responsavel) =>
    responsavel?.email ||
    responsavel?.usuario?.email ||
    responsavel?.login ||
    "";

  const nomeResponsavelPorId = (usuarioId) => {
    if (!usuarioId) return "Não informado";

    const responsavel = responsaveis.find(
      (item) => String(getResponsavelId(item)) === String(usuarioId)
    );

    return responsavel ? getResponsavelNome(responsavel) : usuarioId;
  };

  const abrirNovo = () => {
    if (!loja?.id) {
      message.warning("Aguarde a identificação da loja.");
      return;
    }

    setCaixaEmEdicao(null);
    setDescricao("");
    setAtivo(true);
    setIdUsuarioSelecionado(undefined);
    setModalVisible(true);
  };

  const abrirEdicao = (caixa) => {
    if (!loja?.id) {
      message.warning("A loja não foi identificada.");
      return;
    }

    setCaixaEmEdicao(caixa);
    setDescricao(caixa?.descricao || "");
    setAtivo(caixa?.ativo === true || caixa?.ativo === 1);
    setIdUsuarioSelecionado(caixa?.idUsuario || undefined);
    setModalVisible(true);
  };

  const fecharModal = () => {
    if (salvando) return;

    setModalVisible(false);
    setCaixaEmEdicao(null);
    setDescricao("");
    setAtivo(true);
    setIdUsuarioSelecionado(undefined);
  };

  const salvar = async () => {
    const descricaoTratada = String(descricao || "").trim();

    if (!loja?.id) {
      message.error("Não foi possível identificar a loja.");
      return;
    }

    if (!descricaoTratada) {
      message.warning("Informe a descrição do caixa.");
      return;
    }

    if (!idUsuarioSelecionado) {
      message.warning("Selecione o usuário responsável pelo caixa.");
      return;
    }

    const payload = {
      descricao: descricaoTratada,
      ativo,
      idUsuario: idUsuarioSelecionado,
      idLoja: loja.id,
    };

    setSalvando(true);

    try {
      if (caixaEmEdicao?.id) {
        await axios.put(
          `${CAIXA_URL}/${caixaEmEdicao.id}`,
          {
            ...payload,
            id: caixaEmEdicao.id,
          },
          getOptions(loja.id)
        );

        message.success("Caixa atualizado com sucesso!");
      } else {
        await axios.post(
          CAIXA_URL,
          payload,
          getOptions(loja.id)
        );

        message.success("Caixa cadastrado com sucesso!");
      }

      setModalVisible(false);
      setCaixaEmEdicao(null);
      setDescricao("");
      setAtivo(true);
      setIdUsuarioSelecionado(undefined);

      await carregarCaixas(loja.id);
    } catch (error) {
      console.error("Erro ao salvar caixa:", error);

      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Erro ao salvar o caixa."
      );
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (caixa) => {
    if (!loja?.id) {
      message.error("Não foi possível identificar a loja.");
      return;
    }

    try {
      await axios.delete(
        `${CAIXA_URL}/${caixa.id}`,
        getOptions(loja.id)
      );

      message.success("Caixa excluído com sucesso!");
      await carregarCaixas(loja.id);
    } catch (error) {
      console.error("Erro ao excluir caixa:", error);

      message.error(
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        "Não foi possível excluir o caixa."
      );
    }
  };

  const alterarStatus = async (caixa, novoStatus) => {
    if (!loja?.id) {
      message.error("Não foi possível identificar a loja.");
      return;
    }

    try {
      await axios.put(
        `${CAIXA_URL}/${caixa.id}`,
        {
          id: caixa.id,
          descricao: caixa.descricao,
          ativo: novoStatus,
          idUsuario: caixa.idUsuario,
          idLoja: loja.id,
        },
        getOptions(loja.id)
      );

      setCaixas((listaAtual) =>
        listaAtual.map((item) =>
          item.id === caixa.id ? { ...item, ativo: novoStatus } : item
        )
      );

      message.success(
        novoStatus
          ? "Caixa ativado com sucesso!"
          : "Caixa desativado com sucesso!"
      );
    } catch (error) {
      console.error("Erro ao alterar status:", error);
      message.error("Não foi possível alterar o status do caixa.");
    }
  };

  const alterarVisualizar = async (caixa, visualizar) => {
    if (!loja?.id) {
      message.error("Não foi possível identificar a loja.");
      return;
    }

    try {
      await axios.put(
        `${CAIXA_URL}/${caixa.id}`,
        {
          id: caixa.id,
          descricao: caixa.descricao,
          ativo: caixa.ativo,
          visualizar: visualizar,
          idUsuario: caixa.idUsuario,
          idLoja: loja.id,
        },
        getOptions(loja.id)
      );

      setCaixas((listaAtual) =>
        listaAtual.map((item) =>
          item.id === caixa.id ? { ...item, visualizar: visualizar } : item
        )
      );

      message.success(
        visualizar
          ? "Visualizar ativado com sucesso!"
          : "Visualizar desativado com sucesso!"
      );
    } catch (error) {
      console.error("Erro ao alterar Visualizar:", error);
      message.error("Não foi possível alterar o Visualizar do caixa.");
    }
  };

  const caixasFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    if (!termo) return caixas;

    return caixas.filter((caixa) => {
      const descricaoCaixa = String(caixa?.descricao || "").toLowerCase();
      const responsavel = String(
        nomeResponsavelPorId(caixa?.idUsuario)
      ).toLowerCase();

      return descricaoCaixa.includes(termo) || responsavel.includes(termo);
    });
  }, [caixas, busca, responsaveis]);

  const colunas = [
    {
      title: "Caixa",
      dataIndex: "descricao",
      key: "descricao",
      render: (texto) => (
        <div style={styles.identificacao}>
          <div style={styles.iconeCaixa}>
            <DollarOutlined />
          </div>

          <div>
            <div style={styles.nomeCaixa}>
              {texto || "Sem descrição"}
            </div>
            <div style={styles.subtituloCaixa}>Ponto de caixa</div>
          </div>
        </div>
      ),
    },
    {
      title: "Responsável",
      dataIndex: "idUsuario",
      key: "idUsuario",
      render: (usuarioId) => (
        <div style={styles.identificacao}>
          <div style={styles.iconeUsuario}>
            <UserOutlined />
          </div>

          <div style={styles.nomeResponsavel}>
            {nomeResponsavelPorId(usuarioId)}
          </div>
        </div>
      ),
    },
    {
      title: "Visualizar",
      dataIndex: "visualizar",
      key: "visualizar",
      width: 180,
      render: (valor, caixa) => {
        const podeVisualizar = valor === true || valor === 1;

        return (
          <Space>
            <Switch
              size="small"
              checked={podeVisualizar}
              disabled={!loja?.id}
              onChange={(checked) => alterarVisualizar(caixa, checked)}
            />

            <Tag color={podeVisualizar ? "green" : "default"}>
              {podeVisualizar ? "ATIVO" : "INATIVO"}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      width: 180,
      render: (valor, caixa) => {
        const estaAtivo = valor === true || valor === 1;

        return (
          <Space>
            <Switch
              size="small"
              checked={estaAtivo}
              disabled={!loja?.id}
              onChange={(checked) => alterarStatus(caixa, checked)}
            />

            <Tag color={estaAtivo ? "green" : "default"}>
              {estaAtivo ? "ATIVO" : "INATIVO"}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: "Ações",
      key: "acoes",
      width: 130,
      align: "right",
      render: (_, caixa) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="text"
              icon={<EditOutlined />}
              disabled={!loja?.id}
              onClick={() => abrirEdicao(caixa)}
              style={{ color: "#000" }}
            />
          </Tooltip>

          <Popconfirm
            title="Excluir este caixa?"
            okText="Excluir"
            cancelText="Cancelar"
            disabled={!loja?.id}
            onConfirm={() => excluir(caixa)}
          >
            <Tooltip title="Excluir">
              <Button
                type="text"
                danger
                disabled={!loja?.id}
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={styles.page}>
      <Card bordered={false} style={styles.card} bodyStyle={{ padding: 0 }}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Gerenciamento de Caixas</div>

            <div style={styles.subtitle}>
              {loadingLoja
                ? "Identificando loja..."
                : loja?.id
                  ? `Caixas vinculados à loja${loja?.nome ? `: ${loja.nome}` : ""}.`
                  : "Loja não identificada."}
            </div>
          </div>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={abrirNovo}
            size="large"
            disabled={!loja?.id || loadingLoja}
            style={{
              backgroundColor: "#000",
              borderColor: "#000",
            }}
          >
            Novo Caixa
          </Button>
        </div>

        <div style={styles.toolbar}>
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
            placeholder="Buscar caixa ou responsável..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ maxWidth: 420 }}
            disabled={!loja?.id}
          />

          <Tooltip title="Atualizar lista">
            <Button
              size="large"
              icon={<ReloadOutlined />}
              style={{
                backgroundColor: "#000",
                borderColor: "#000",
                color: "#fff",
              }}
              onClick={() => {
                if (loja?.id) {
                  carregarCaixas(loja.id);
                  carregarResponsaveis(loja.id);
                }
              }}
              loading={loading || loadingResponsaveis}
              disabled={!loja?.id}
            />
          </Tooltip>
        </div>

        <div style={styles.tableContainer}>
          <Table
            rowKey="id"
            columns={colunas}
            dataSource={caixasFiltrados}
            loading={loading || loadingLoja}
            size="middle"
            locale={{
              emptyText: loja?.id
                ? "Nenhum caixa cadastrado para esta loja."
                : "Loja não identificada.",
            }}
            pagination={
              caixasFiltrados.length > 10
                ? {
                  pageSize: 10,
                  showSizeChanger: false,
                  showTotal: (total) => `${total} caixa(s)`,
                }
                : false
            }
          />
        </div>
      </Card>

      <Modal
        title={caixaEmEdicao ? "Editar Caixa" : "Novo Caixa"}
        visible={modalVisible}
        onCancel={fecharModal}
        onOk={salvar}
        okText={caixaEmEdicao ? "Salvar alterações" : "Cadastrar"}
        cancelText="Cancelar"
        okButtonProps={{
          style: {
            backgroundColor: "#000",
            borderColor: "#000",
            color: "#fff",
          },
        }}
        cancelButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "#fff",
          },
        }}
        confirmLoading={salvando}
        destroyOnClose
      >
        <div style={{ marginBottom: 20 }}>
          <div style={styles.label}>Descrição</div>

          <Input
            autoFocus
            maxLength={250}
            size="large"
            placeholder="Ex.: Caixa principal"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
          />

          <div style={styles.contador}>{descricao.length}/250</div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={styles.label}>Usuário responsável</div>

          <Select
            showSearch
            allowClear
            size="large"
            style={{ width: "100%" }}
            placeholder="Selecione o usuário..."
            value={idUsuarioSelecionado}
            loading={loadingResponsaveis}
            disabled={loadingResponsaveis || !loja?.id}
            onChange={setIdUsuarioSelecionado}
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.children || "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {responsaveis.map((responsavel) => {
              const usuarioId = getResponsavelId(responsavel);
              const nome = getResponsavelNome(responsavel);
              const email = getResponsavelEmail(responsavel);

              if (!usuarioId) return null;

              return (
                <Option key={usuarioId} value={usuarioId}>
                  {email ? `${nome} - ${email}` : nome}
                </Option>
              );
            })}
          </Select>

          {!loadingResponsaveis && responsaveis.length === 0 && (
            <div style={styles.avisoResponsaveis}>
              Nenhum usuário responsável encontrado para esta loja.
            </div>
          )}
        </div>

        <div style={styles.statusBox}>
          <div>
            <div style={styles.statusTitle}>Caixa ativo</div>
            <div style={styles.statusDescription}>
              Define se o caixa poderá ser utilizado nas operações.
            </div>
          </div>

          <Switch checked={ativo} onChange={setAtivo} />
        </div>
      </Modal>
    </div>
  );
};

const styles = {
  page: {
    width: "100%",
    minHeight: "100%",
    padding: 24,
    background: "#f5f7fa",
    overflowY: "auto",
  },
  card: {
    maxWidth: 1200,
    margin: "0 auto",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.06)",
  },
  header: {
    padding: "24px 26px 20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    borderBottom: "1px solid #f0f0f0",
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: "#1f2937",
  },
  subtitle: {
    marginTop: 4,
    color: "#8c8c8c",
    fontSize: 14,
  },
  toolbar: {
    padding: "18px 26px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    background: "#fafafa",
  },
  tableContainer: {
    padding: "0 26px 26px",
  },
  identificacao: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  iconeCaixa: {
    width: 42,
    height: 42,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f0f5ff",
    color: "#1677ff",
    fontSize: 18,
  },
  iconeUsuario: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f6ffed",
    color: "#52c41a",
    fontSize: 15,
  },
  nomeCaixa: {
    color: "#1f2937",
    fontWeight: 600,
  },
  nomeResponsavel: {
    color: "#374151",
    fontWeight: 500,
  },
  subtituloCaixa: {
    color: "#9ca3af",
    fontSize: 12,
    marginTop: 2,
  },
  label: {
    fontWeight: 600,
    color: "#262626",
    marginBottom: 8,
  },
  contador: {
    marginTop: 4,
    textAlign: "right",
    color: "#bfbfbf",
    fontSize: 11,
  },
  avisoResponsaveis: {
    marginTop: 6,
    fontSize: 12,
    color: "#fa8c16",
  },
  statusBox: {
    padding: 16,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
    borderRadius: 12,
    border: "1px solid #f0f0f0",
    background: "#fafafa",
  },
  statusTitle: {
    fontWeight: 600,
    color: "#262626",
  },
  statusDescription: {
    color: "#8c8c8c",
    fontSize: 12,
    marginTop: 2,
  },
};

export default Caixas;
